import {
  Award,
  BrainCircuit,
  Building2,
  Cog,
  Database,
  DoorOpen,
  FileCode2,
  FileSignature,
  FileText,
  GitCompareArrows,
  Inbox,
  KeyRound,
  Landmark,
  Layers,
  Mail,
  Package,
  Reply,
  Ruler,
  Shield,
  ShieldCheck,
  Sparkles,
  User,
  type LucideIcon,
} from "lucide-react";

import type { FeatureId, ModuleId } from "@/lib/client-profile";
import { CALENDAR_MODULES, COMPANY_PROFILE_MODULES } from "@/lib/menu-sections";
import { canManageRooms } from "@/lib/room-access";
import { ROUTES } from "@/routes/paths";

/**
 * O catálogo das definições: tudo o que se configura, agrupado por assunto.
 *
 * Cada destino declara quem o pode ver, e essa regra tem de ser a MESMA que a
 * rota exige (ver `routes/modules/settings.routes.tsx`) — se aqui for mais
 * permissiva, a pessoa clica e bate num "acesso negado", que é a pior forma de
 * descobrir que não tem permissão.
 *
 * É dado puro (sem React): a página, o rasto de navegação e os testes leem-no
 * daqui, e há um sítio só onde se acrescenta uma definição.
 */

export interface AccessContext {
  isAdmin: boolean;
  isSuperuser: boolean;
  can: (entity: string, action: string) => boolean;
  hasModule: (module: ModuleId) => boolean;
  hasFeature: (feature: FeatureId) => boolean;
}

export interface SettingsItem {
  path: string;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Palavras que levam aqui na pesquisa e que não estão no nome (sinónimos, o que lá se faz). */
  keywords?: string[];
  /**
   * O módulo a que este destino pertence. Ausente = núcleo (a conta, a
   * auditoria) — existe em qualquer instalação.
   *
   * ⚠️ **Sem isto, a permissão sozinha não chega.** Um cliente que não comprou
   * Produção não tem o módulo, mas o `is_admin_user` dele responde `true` a
   * `can("machines", "READ")` — e via Máquinas e Acessórios nesta página. A
   * permissão responde a «este utilizador pode?»; só o módulo responde a
   * «este cliente comprou?», e é a segunda pergunta que decide primeiro.
   */
  module?: ModuleId;
  /**
   * "Qualquer um destes módulos" — para o que serve vários (as salas de reunião
   * existem onde o Calendário existe). Se estiver presente, decide no lugar de `module`.
   */
  anyModule?: readonly string[];
  /** A funcionalidade do perfil que a rota também exige. */
  feature?: FeatureId;
  /** Quem vê isto. Espelha a permissão da rota. */
  visible: (ctx: AccessContext) => boolean;
}

export interface SettingsGroup {
  id: string;
  title: string;
  /** Uma frase que diz para que serve o grupo — aparece no topo dele. */
  description: string;
  icon: LucideIcon;
  items: SettingsItem[];
}

/** Exportado para os testes poderem verificar os gates. */
export const SETTINGS_SECTIONS: SettingsGroup[] = [
  {
    id: "conta",
    title: "Conta",
    description: "Os teus dados, notificações e palavra-passe.",
    icon: User,
    items: [
      {
        path: ROUTES.SETTINGS.ACCOUNT,
        label: "A minha conta",
        description: "Dados pessoais, notificações, documentos e palavra-passe.",
        icon: User,
        keywords: ["perfil", "palavra-passe", "password", "foto", "notificações"],
        visible: () => true,
      },
    ],
  },
  {
    id: "empresa",
    title: "Empresa",
    description: "Quem somos e os espaços que a empresa partilha.",
    icon: Building2,
    items: [
      {
        path: ROUTES.SETTINGS.COMPANY,
        label: "Dados da empresa",
        description: "Nome, NIF, morada e contactos que aparecem nos documentos e nos e-mails.",
        icon: Building2,
        keywords: ["nif", "morada", "sede", "logótipo", "identidade"],
        anyModule: COMPANY_PROFILE_MODULES,
        visible: ({ isAdmin }) => isAdmin,
      },
      {
        path: ROUTES.SETTINGS.MEETING_ROOMS,
        label: "Salas de reunião",
        description: "As salas que se reservam no Calendário: capacidade, localização e equipamento.",
        icon: DoorOpen,
        keywords: ["reservas", "espaços", "calendário"],
        anyModule: CALENDAR_MODULES,
        // A MESMA regra da rota e do servidor (lib/room-access): administrador ou calendar_events.CREATE.
        visible: ({ isAdmin, can }) => canManageRooms(isAdmin, can),
      },
    ],
  },
  {
    id: "acessos",
    title: "Acessos e segurança",
    description: "Quem pode ver e fazer o quê, e o rasto do que mudou.",
    icon: ShieldCheck,
    items: [
      {
        path: ROUTES.SETTINGS.PERMISSIONS,
        label: "Permissões",
        description: "Quem pode ver e fazer o quê, por departamento e por pessoa.",
        icon: KeyRound,
        keywords: ["acessos", "departamentos", "papéis", "exceções"],
        visible: ({ isAdmin }) => isAdmin,
      },
      {
        path: ROUTES.SETTINGS.AUDIT_LOG,
        label: "Registo de auditoria",
        description: "Histórico de alterações de permissões: quem mudou o quê e quando.",
        icon: Shield,
        keywords: ["log", "histórico", "alterações"],
        visible: ({ isAdmin }) => isAdmin,
      },
    ],
  },
  {
    id: "comunicacao",
    title: "Comunicação",
    description: "As caixas de correio que a plataforma lê e como responde.",
    icon: Mail,
    items: [
      {
        path: ROUTES.SETTINGS.EMAIL_ACCOUNTS,
        label: "Contas de e-mail",
        description: "As caixas de correio que alimentam a Triagem de E-mail.",
        icon: Inbox,
        keywords: ["imap", "caixa de correio", "triagem"],
        module: "comercial",
        feature: "comercial.email_triage",
        visible: ({ can }) => can("email_accounts", "READ"),
      },
      {
        path: ROUTES.SETTINGS.EMAIL_AUTO_REPLY,
        label: "Resposta automática",
        description: "A mensagem que sai sozinha quando chega um e-mail à triagem.",
        icon: Reply,
        keywords: ["auto-resposta", "ausência", "modelo"],
        module: "comercial",
        feature: "comercial.email_triage",
        visible: ({ can }) => can("email_triage", "READ"),
      },
    ],
  },
  {
    id: "ia",
    title: "Inteligência artificial",
    description: "O que o assistente consome e quanto custa.",
    icon: Sparkles,
    items: [
      {
        path: ROUTES.SETTINGS.AI_USAGE,
        label: "IA — uso e custos",
        description: "Consumo, custo e modelos do assistente, por função.",
        icon: BrainCircuit,
        keywords: ["tokens", "modelo", "openrouter", "assistente", "custos"],
        // A rota exige superuser — não basta ser administrador.
        visible: ({ isSuperuser }) => isSuperuser,
      },
    ],
  },
  {
    id: "dados-mestre",
    title: "Dados mestre",
    description: "As listas de base que o resto da plataforma usa.",
    icon: Database,
    items: [
      {
        path: ROUTES.SETTINGS.MASTER_DATA.MACHINES.LIST,
        label: "Máquinas",
        description: "O parque de máquinas da fábrica.",
        icon: Cog,
        module: "producao",
        visible: ({ can }) => can("machines", "READ"),
      },
      {
        path: ROUTES.SETTINGS.MASTER_DATA.RAW_MATERIALS.LIST,
        label: "Matérias-primas",
        description: "Fios, malhas e restantes matérias-primas.",
        icon: Layers,
        module: "artigos",
        visible: ({ can }) => can("products", "READ"),
      },
      {
        path: ROUTES.SETTINGS.MASTER_DATA.ACCESSORIES.LIST,
        label: "Acessórios",
        description: "Etiquetas, embalagens e outros acessórios.",
        icon: Package,
        module: "producao",
        visible: ({ can }) => can("accessories", "READ"),
      },
      {
        path: ROUTES.SETTINGS.MASTER_DATA.MEASUREMENTS,
        label: "Medidas",
        description: "Tabelas de medidas por tamanho.",
        icon: Ruler,
        module: "artigos",
        visible: ({ can }) => can("products", "READ"),
      },
      {
        path: ROUTES.SETTINGS.MASTER_DATA.UNITS,
        label: "Unidades",
        description: "Unidades de medida usadas nos produtos.",
        icon: Ruler,
        module: "artigos",
        visible: ({ can }) => can("products", "READ"),
      },
      {
        path: ROUTES.SETTINGS.MASTER_DATA.CERTIFICATES,
        label: "Certificados",
        description: "Certificações aplicáveis aos produtos.",
        icon: Award,
        module: "artigos",
        visible: ({ can }) => can("products", "READ"),
      },
    ],
  },
  {
    id: "documentos",
    title: "Documentos",
    description: "Os modelos dos documentos que a plataforma emite.",
    icon: FileText,
    items: [
      {
        path: ROUTES.SETTINGS.DECLARATIONS,
        label: "Declarações",
        description: "Modelos das declarações de trabalho e assinatura.",
        icon: FileSignature,
        keywords: ["modelos", "assinatura", "declaração de trabalho"],
        module: "hr",
        visible: ({ can }) => can("declarations", "UPDATE"),
      },
    ],
  },
  {
    id: "folha",
    title: "Folha de vencimentos",
    description: "As regras que alimentam o ficheiro para o Primavera.",
    icon: Landmark,
    items: [
      {
        path: ROUTES.HR.PRIMAVERA_RULES,
        label: "Regras Primavera",
        description: "Escalões de horas extra, códigos e feriados que entram no ficheiro para o Primavera.",
        icon: FileCode2,
        keywords: ["horas extra", "feriados", "códigos", "salários"],
        module: "hr",
        // A MESMA regra da rota (`access: "admin"`).
        visible: ({ isAdmin }) => isAdmin,
      },
      {
        path: ROUTES.SETTINGS.PRIMAVERA_COMPARE,
        label: "Comparar com o Primavera",
        description: "Carregar o ficheiro do Primavera e ver o que falta, sobra ou diverge face ao calculado aqui.",
        icon: GitCompareArrows,
        keywords: ["conferir", "diferenças", "salários"],
        module: "hr",
        visible: ({ can }) => can("attendance", "CREATE"),
      },
    ],
  },
];

/** O destino aparece a este utilizador, neste cliente? Módulo e funcionalidade antes da permissão. */
export function isItemAvailable(item: SettingsItem, ctx: AccessContext): boolean {
  const moduleOk = item.anyModule ? item.anyModule.some((m) => ctx.hasModule(m as ModuleId)) : !item.module || ctx.hasModule(item.module);
  if (!moduleOk) return false;
  if (item.feature && !ctx.hasFeature(item.feature)) return false;
  return item.visible(ctx);
}

/** Os grupos que sobram a quem pergunta — um grupo sem destinos nenhuns não se mostra. */
export function availableGroups(ctx: AccessContext, groups: SettingsGroup[] = SETTINGS_SECTIONS): SettingsGroup[] {
  return groups.map((g) => ({ ...g, items: g.items.filter((i) => isItemAvailable(i, ctx)) })).filter((g) => g.items.length > 0);
}

const semAcentos = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/** Filtra por texto, sem olhar a acentos nem a maiúsculas: nome, frase, sinónimos e nome do grupo. */
export function searchGroups(groups: SettingsGroup[], query: string): SettingsGroup[] {
  const termos = semAcentos(query).split(/\s+/).filter(Boolean);
  if (termos.length === 0) return groups;
  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => {
        const palheiro = semAcentos([i.label, i.description, g.title, ...(i.keywords ?? [])].join(" "));
        return termos.every((t) => palheiro.includes(t));
      }),
    }))
    .filter((g) => g.items.length > 0);
}

/** Um rasto para uma rota de definições que já não está no menu lateral: «Definições › Permissões». */
export function settingsLabelForPath(pathname: string): string | undefined {
  const itens = SETTINGS_SECTIONS.flatMap((g) => g.items).filter((i) => i.path.startsWith(ROUTES.SETTINGS.MAIN));
  // O mais específico ganha: `/dashboard/settings/machines/42` é uma ficha das Máquinas.
  const match = itens
    .filter((i) => pathname === i.path || pathname.startsWith(i.path + "/"))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return match?.label;
}
